/**
 * Общие словари и форматтеры для сгенерированных DOCX (composed_responses).
 *
 * Вынесено сюда, чтобы страница архива (pages/Statut/ComposedDocs) и
 * карточка отдельного документа (pages/Statut/DocumentPage) использовали
 * один источник истины для названий типов, бейджей и форматирования
 * размера/даты. Раньше KIND_BADGE и formatSize жили локально в ComposedDocs —
 * при добавлении страницы документа их пришлось бы дублировать и держать
 * синхронными вручную.
 */

/** Тон бейджа типа документа — совпадает с пропом Badge `$tone`. */
export type ComposedKindTone = 'info' | 'warning' | 'success' | 'danger';

/** Полные человекочитаемые названия типов сгенерированных документов.
 *  Ключи — source_template_kind (он же AuditScope для composed-документов).
 *  Используются как заголовок карточки документа и как entityLabel при
 *  открытии модалки внешнего аудита. */
export const KIND_LABELS: Record<string, string> = {
  response_claim: 'Ответ на претензию',
  outgoing_claim: 'Перевыставление субподрядчику',
  protocol_disagreement: 'Протокол разногласий',
  lawsuit: 'Иск',
};

/** Короткие подписи + тон для бейджа в плотных списках (таблица архива). */
export const KIND_BADGE: Record<string, { label: string; tone: ComposedKindTone }> = {
  response_claim: { label: 'Ответ', tone: 'info' },
  outgoing_claim: { label: 'Перевыставление', tone: 'warning' },
  protocol_disagreement: { label: 'Протокол', tone: 'success' },
  lawsuit: { label: 'Иск', tone: 'danger' },
};

/** Человекочитаемое название типа по source_template_kind с безопасным
 *  фолбэком (для неизвестных/новых видов шаблонов показываем сам код). */
export function composedKindLabel(kind: string | null | undefined): string {
  if (!kind) return 'Документ';
  return KIND_LABELS[kind] ?? kind;
}

/** Размер файла в человекочитаемом виде (Б / КБ / МБ). */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

/** Дата+время создания DOCX в локали ru-RU (DD.MM.YYYY HH:mm). */
export function formatComposedDt(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
